import { createSlice } from '@reduxjs/toolkit'

import * as localStore from '@/utils/localStore'

import AsyncState from '../utils/AsyncState'

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    verifyWithSocialProvider: AsyncState.reset(),
    authUserID: localStore.getCustomerID(),
    role: localStore.getRole(),
    expire: localStore.getExpire(),
    email: null,
    password: null,
    sendingOTP: false,
    sentOTP: false,
    sendOTPError: null,
    verifyingOTP: false,
    verified: false,
    verifyOTPError: null,
    verifyEmailDone: false,
    resetPasswordSendDone: false,
    verifyResetPasswordDone: false,
    resetPasswordDone: false,
    logoutDone: false,
    loading: false,
    done: false,
    error: null,
    skipKyc: null,
  },
  reducers: {
    reset: (state) => {
      state.verifyWithSocialProvider = AsyncState.reset()
    },
    verifyWithSocialProviderStart: (state) => {
      state.verifyWithSocialProvider = AsyncState.start()
    },
    verifyWithSocialProviderSuccess: (state, action) => {
      state.verifyWithSocialProvider = AsyncState.success(action.payload)
    },
    verifyWithSocialProviderFailed: (state, action) => {
      state.verifyWithSocialProvider = AsyncState.failed(action.payload)
    },
    signUpStart: (state, action) => {
      const {
        payload: { email, password },
      } = action
      state.email = email
      state.password = password
      state.loading = true
      state.done = false
      state.error = null
    },
    signUpSuccess: (state) => {
      state.loading = false
      state.done = true
    },
    signUpFailed: (state, action) => {
      state.loading = false
      state.done = true
      state.error = action.payload
    },
    sendOTPStart: (state) => {
      state.sendingOTP = true
      state.sentOTP = false
      state.sendOTPError = null
    },
    sendOTPSuccess: (state) => {
      state.sendingOTP = false
      state.sentOTP = true
    },
    sendOTPFailed: (state, action) => {
      state.sendingOTP = false
      state.sendOTPError = action.payload
    },
    verifyOTPStart: (state) => {
      state.verifyingOTP = true
      state.verifyOTPError = null
    },
    verifyOTPSuccess: (state) => {
      state.verifyingOTP = false
      state.verified = true
    },
    verifyOTPFailed: (state, action) => {
      state.verifyingOTP = false
      state.verifyOTPError = action.payload
    },
    clearSignUp: (state) => {
      localStore.remove()
      state.email = null
      state.password = null
      state.sendingOTP = false
      state.sentOTP = false
      state.sendOTPError = null
      state.verifyingOTP = false
      state.verified = false
      state.verifyOTPError = null
      state.loading = false
      state.done = false
      state.error = null
    },
    loginStart: (state) => {
      state.loading = true
      state.error = null
    },
    loginSuccess: (state, action) => {
      state.authUserID = localStore.getCustomerID()
      state.role = localStore.getRole()
      state.expire = localStore.getExpire()
      state.loading = false
      state.error = null
    },
    loginFailed: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    loginClear: (state) => {
      state.loading = false
      state.error = null
    },
    logoutClear: (state) => {
      state.logoutDone = false
    },
    logout: (state) => {
      localStore.remove()
      state.authUserID = null
      state.role = null
      state.expire = null
      state.logoutDone = true
    },
    verifyEmailClear: (state) => {
      state.loading = false
      state.error = null
      state.verifyEmailDone = false
    },
    verifyEmailStart: (state) => {
      state.loading = true
      state.verifyEmailDone = false
    },
    verifyEmailSuccess: (state) => {
      state.loading = false
      state.verifyEmailDone = true
    },
    verifyEmailFailed: (state, action) => {
      state.loading = false
      state.verifyEmailDone = false
      state.error = action.payload
    },
    resetPasswordSendClear: (state) => {
      state.loading = false
      state.error = null
      state.resetPasswordSendDone = false
    },
    resetPasswordSendStart: (state) => {
      state.loading = true
      state.resetPasswordSendDone = false
    },
    resetPasswordSendSuccess: (state) => {
      state.loading = false
      state.resetPasswordSendDone = true
    },
    resetPasswordSendFailed: (state, action) => {
      state.loading = false
      state.resetPasswordSendDone = false
      state.error = action.payload
    },
    verifyResetPasswordClear: (state) => {
      state.loading = false
      state.error = null
      state.verifyResetPasswordDone = false
    },
    verifyResetPasswordStart: (state) => {
      state.loading = true
      state.verifyResetPasswordDone = false
    },
    verifyResetPasswordSuccess: (state) => {
      state.loading = false
      state.verifyResetPasswordDone = true
    },
    verifyResetPasswordFailed: (state, action) => {
      state.loading = false
      state.verifyResetPasswordDone = false
      state.error = action.payload
    },
    resetPasswordClear: (state) => {
      state.loading = false
      state.error = null
      state.resetPasswordDone = false
    },
    resetPasswordStart: (state) => {
      state.loading = true
      state.resetPasswordDone = false
    },
    resetPasswordSuccess: (state) => {
      state.loading = false
      state.resetPasswordDone = true
    },
    resetPasswordFailed: (state, action) => {
      state.loading = false
      state.resetPasswordDone = false
      state.error = action.payload
    },
    skipKycStart: (state) => {
      state.skipKyc = AsyncState.start()
      state.loading = true
    },
    skipKycSuccess: (state, action) => {
      state.skipKyc = AsyncState.success(action.payload)
      state.loading = false
    },
    skipKycFailed: (state, action) => {
      state.skipKyc = AsyncState.failed(action.payload)
      state.loading = false
    },
  },
})

export const {
  signUpStart,
  signUpSuccess,
  signUpFailed,
  sendOTPStart,
  sendOTPSuccess,
  sendOTPFailed,
  verifyOTPStart,
  verifyOTPSuccess,
  verifyOTPFailed,
  clearSignUp,
  loginStart,
  loginSuccess,
  loginFailed,
  logout,
} = authSlice.actions

export const { actions } = authSlice

export default authSlice.reducer
